import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ResponsiveNavigation from '../components/ResponsiveNavigation';
import RichTextEditor from '../components/RichTextEditor';
import { Save, X } from 'lucide-react';
import { TECH_SKILLS, LANGUAGES } from '../constants/skills';
import GeoPublishModal from '../components/GeoPublishModal';
import HighlightMatch from '../components/HighlightMatch';

// (Skill + language catalogues live in /app/frontend/src/constants/skills.js)

const AddPosition = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    numberOfPositions: 1,
    experienceLevel: '',
    salaryMin: '',
    salaryMax: '',
    salaryCurrency: 'EUR',
    noticePeriod: '',
    employmentType: '',
    workplace: '',
    industry: '',
    location: '',
    mustHaveSkills: [],
    niceToHaveSkills: [],
    languages: [],
    companyName: '',
    companyEmail: '',
    contactName: '',
    contactPhone: '',
    contactTitle: '',
    description: '',
    responsibilities: '',
    profileRequirements: '',
    benefits: '',
    closesAt: '',
    status: 'draft'
  });

  const [searchInputs, setSearchInputs] = useState({
    mustHave: '',
    niceToHave: '',
    language: ''
  });

  // ── Publish-to-job-boards modal state ───────────────────────────
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [defaultPublishTargets, setDefaultPublishTargets] = useState([]);

  // Fetch company's default publish targets once on mount (admin-set in Settings).
  useEffect(() => {
    if (!userProfile?.organizationId) return;
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${userProfile.organizationId}/publish-defaults`)
      .then((r) => setDefaultPublishTargets(r.data?.targets || []))
      .catch(() => setDefaultPublishTargets([]));
  }, [userProfile?.organizationId]);

  // Auto-prefill company name from the recruiter's organization
  useEffect(() => {
    if (userProfile?.organizationName && !formData.companyName) {
      setFormData(prev => ({ ...prev, companyName: userProfile.organizationName }));
    }
  }, [userProfile?.organizationName]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleRichTextChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // Skill management
  const addSkill = (type, skill) => {
    if (skill && !formData[type].includes(skill)) {
      setFormData({
        ...formData,
        [type]: [...formData[type], skill]
      });
      setSearchInputs({ ...searchInputs, [type === 'mustHaveSkills' ? 'mustHave' : 'niceToHave']: '' });
    }
  };

  const removeSkill = (type, index) => {
    setFormData({
      ...formData,
      [type]: formData[type].filter((_, i) => i !== index)
    });
  };

  // Language management
  const addLanguage = (language) => {
    if (language && !formData.languages.includes(language)) {
      setFormData({
        ...formData,
        languages: [...formData.languages, language]
      });
      setSearchInputs({ ...searchInputs, language: '' });
    }
  };

  const removeLanguage = (index) => {
    setFormData({
      ...formData,
      languages: formData.languages.filter((_, i) => i !== index)
    });
  };

  const getFilteredSkills = (searchTerm, excludeList) => {
    return TECH_SKILLS.filter(skill =>
      skill.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !excludeList.includes(skill)
    );
  };

  const getFilteredLanguages = () => {
    return LANGUAGES.filter(lang =>
      lang.toLowerCase().includes(searchInputs.language.toLowerCase()) &&
      !formData.languages.includes(lang)
    );
  };

  const handleSubmit = async (status, publishTargets = [], publishDays = {}) => {
    try {
      setLoading(true);
      
      if (!userProfile?.organizationId) {
        alert('Error: No organization associated with your account. Please contact support.');
        setLoading(false);
        return;
      }
      
      const positionData = {
        ...formData,
        status,
        organizationId: userProfile.organizationId,
        createdByUserId: userProfile.id,
        salary_min: parseInt(formData.salaryMin) || null,
        salary_max: parseInt(formData.salaryMax) || null,
        number_of_positions: parseInt(formData.numberOfPositions) || 1,
        must_have_skills: formData.mustHaveSkills,
        nice_to_have_skills: formData.niceToHaveSkills,
        profile_requirements: formData.profileRequirements,
        benefits: formData.benefits,
        closes_at: formData.closesAt || null,
        publish_targets: status === 'published' ? publishTargets : [],
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/positions`,
        positionData
      );

      if (response.data.success) {
        const newId = response.data?.data?.id;
        if (status === 'published' && newId && publishDays && Object.keys(publishDays).length) {
          try {
            await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/positions/publish-days`, {
              organizationId: userProfile.organizationId,
              positionId: newId,
              days: publishDays,
            });
          } catch (e) { /* non-blocking */ }
        }
        if (status === 'published') {
          const n = publishTargets.length;
          alert(`Position published${n ? ` (selected for ${n} job board${n > 1 ? 's' : ''})` : ''}!`);
        } else {
          alert('Position saved as draft!');
        }
        navigate('/positions');
      }
    } catch (error) {
      console.error('Error creating position:', error);
      if (error.response) {
        alert(`Failed to create position: ${error.response.data.message || 'Please try again.'}`);
      } else {
        alert('Failed to create position. Please check console for details.');
      }
    } finally {
      setLoading(false);
      setShowPublishModal(false);
    }
  };

  // Opens the publish modal first (with admin defaults pre-selected),
  // then submits with whichever boards the recruiter ticked.
  const handlePublishClick = () => {
    if (!formData.title) {
      alert('Please enter a position title before publishing.');
      return;
    }
    setShowPublishModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ResponsiveNavigation />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Add New Position</h1>
          <p className="text-gray-600 mt-2">Fill in the details to create a new job posting</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-8">
          <form className="space-y-6">
            {/* Basic Information */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Position Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Senior Frontend Developer"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Positions
                  </label>
                  <input
                    type="number"
                    name="numberOfPositions"
                    value={formData.numberOfPositions}
                    onChange={handleInputChange}
                    min="1"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Closes on
                  </label>
                  <input
                    type="date"
                    name="closesAt"
                    value={formData.closesAt}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Auto-closes after 90 days if not set</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experience Level
                  </label>
                  <select
                    name="experienceLevel"
                    value={formData.experienceLevel}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select level</option>
                    <option value="junior">Junior</option>
                    <option value="mid">Mid-Level</option>
                    <option value="senior">Senior</option>
                    <option value="lead">Lead</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Salary Min (Annual)
                  </label>
                  <input
                    type="number"
                    name="salaryMin"
                    value={formData.salaryMin}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="50000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Salary Max (Annual)
                  </label>
                  <input
                    type="number"
                    name="salaryMax"
                    value={formData.salaryMax}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="80000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notice Period
                  </label>
                  <select
                    name="noticePeriod"
                    value={formData.noticePeriod}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select notice period</option>
                    <option value="immediate">Immediate</option>
                    <option value="1 month">1 Month</option>
                    <option value="2 months">2 Months</option>
                    <option value="3 months">3 Months</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employment Type
                  </label>
                  <select
                    name="employmentType"
                    value={formData.employmentType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select type</option>
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="freelance">Freelance</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Workplace
                  </label>
                  <select
                    name="workplace"
                    value={formData.workplace}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select workplace</option>
                    <option value="on-site">On-site</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="remote">Remote</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry
                  </label>
                  <input
                    type="text"
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Technology, Finance"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Berlin, Germany"
                  />
                </div>
              </div>
            </div>

            {/* Skills & Languages */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Skills & Languages</h2>
              
              {/* Must-Have Skills */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Must-Have Skills
                </label>
                <div className="flex flex-wrap gap-2 mb-2 min-h-[40px] border border-gray-200 rounded-md p-2">
                  {formData.mustHaveSkills.length === 0 ? (
                    <span className="text-gray-400 text-sm">No skills selected</span>
                  ) : (
                    formData.mustHaveSkills.map((skill, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm"
                      >
                        {skill}
                        <button
                          type="button"
                          onClick={() => removeSkill('mustHaveSkills', idx)}
                          className="hover:text-blue-900"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))
                  )}
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={searchInputs.mustHave}
                    onChange={(e) => setSearchInputs({ ...searchInputs, mustHave: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Search for skills..."
                  />
                  {searchInputs.mustHave && getFilteredSkills(searchInputs.mustHave, formData.mustHaveSkills).length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {getFilteredSkills(searchInputs.mustHave, formData.mustHaveSkills).slice(0, 10).map((skill) => (
                        <button
                          key={skill}
                          type="button"
                          onClick={() => addSkill('mustHaveSkills', skill)}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm"
                        >
                          <HighlightMatch text={skill} query={searchInputs.mustHave} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Nice-to-Have Skills */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nice-to-Have Skills
                </label>
                <div className="flex flex-wrap gap-2 mb-2 min-h-[40px] border border-gray-200 rounded-md p-2">
                  {formData.niceToHaveSkills.length === 0 ? (
                    <span className="text-gray-400 text-sm">No skills selected</span>
                  ) : (
                    formData.niceToHaveSkills.map((skill, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        {skill}
                        <button
                          type="button"
                          onClick={() => removeSkill('niceToHaveSkills', idx)}
                          className="hover:text-gray-900"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))
                  )}
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={searchInputs.niceToHave}
                    onChange={(e) => setSearchInputs({ ...searchInputs, niceToHave: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Search for skills..."
                  />
                  {searchInputs.niceToHave && getFilteredSkills(searchInputs.niceToHave, formData.niceToHaveSkills).length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {getFilteredSkills(searchInputs.niceToHave, formData.niceToHaveSkills).slice(0, 10).map((skill) => (
                        <button
                          key={skill}
                          type="button"
                          onClick={() => addSkill('niceToHaveSkills', skill)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                        >
                          <HighlightMatch text={skill} query={searchInputs.niceToHave} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Languages */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Required Languages
                </label>
                <div className="flex flex-wrap gap-2 mb-2 min-h-[40px] border border-gray-200 rounded-md p-2">
                  {formData.languages.length === 0 ? (
                    <span className="text-gray-400 text-sm">No languages selected</span>
                  ) : (
                    formData.languages.map((lang, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm"
                      >
                        {lang}
                        <button
                          type="button"
                          onClick={() => removeLanguage(idx)}
                          className="hover:text-green-900"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))
                  )}
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={searchInputs.language}
                    onChange={(e) => setSearchInputs({ ...searchInputs, language: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Search for languages..."
                  />
                  {searchInputs.language && getFilteredLanguages().length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {getFilteredLanguages().slice(0, 10).map((lang) => (
                        <button
                          key={lang}
                          type="button"
                          onClick={() => addLanguage(lang)}
                          className="w-full text-left px-3 py-2 hover:bg-green-50 text-sm"
                        >
                          <HighlightMatch text={lang} query={searchInputs.language} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Company Contact */}
            <div className="border-b border-gray-200 pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Company Contact Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    readOnly
                    title="This is your organization name and cannot be changed here."
                    className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 cursor-not-allowed focus:outline-none"
                    data-testid="add-company-name"
                  />
                  <p className="text-xs text-gray-400 mt-1">Set from your organization profile</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Email
                  </label>
                  <input
                    type="email"
                    name="companyEmail"
                    value={formData.companyEmail}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Title
                  </label>
                  <input
                    type="text"
                    name="contactTitle"
                    value={formData.contactTitle}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., HR Manager"
                  />
                </div>
              </div>
            </div>

            {/* Job Description Section - NEW STRUCTURE */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Job Details</h2>
              
              <div className="space-y-6">
                {/* Job Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Description
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Provide a general overview of the role and what makes it exciting
                  </p>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(value) => handleRichTextChange('description', value)}
                    placeholder="Describe the role, team, and what makes this opportunity unique..."
                    height="120px"
                  />
                </div>

                {/* Responsibilities */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Responsibilities
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Key tasks and duties (use bullet points for clarity)
                  </p>
                  <RichTextEditor
                    value={formData.responsibilities}
                    onChange={(value) => handleRichTextChange('responsibilities', value)}
                    placeholder="• Design and develop user interfaces&#10;• Collaborate with cross-functional teams&#10;• Write clean, maintainable code..."
                    height="150px"
                    autoBullets={true}
                  />
                </div>

                {/* Profile / Requirements */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profile (Required Skills & Experience)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    What qualifications and experience are you looking for?
                  </p>
                  <RichTextEditor
                    value={formData.profileRequirements}
                    onChange={(value) => handleRichTextChange('profileRequirements', value)}
                    placeholder="• 5+ years of experience in software development&#10;• Strong knowledge of React and TypeScript&#10;• Excellent communication skills..."
                    height="150px"
                    autoBullets={true}
                  />
                </div>

                {/* Benefits */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Benefits
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    What does the company offer? (perks, culture, growth opportunities)
                  </p>
                  <RichTextEditor
                    value={formData.benefits}
                    onChange={(value) => handleRichTextChange('benefits', value)}
                    placeholder="• Competitive salary and equity&#10;• Flexible working hours&#10;• Health insurance&#10;• Learning & development budget..."
                    height="120px"
                    autoBullets={true}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => handleSubmit('draft')}
                disabled={loading}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-md font-medium disabled:bg-gray-100 disabled:text-gray-400"
              >
                <div className="flex items-center justify-center gap-2">
                  <Save className="w-5 h-5" />
                  {loading ? 'Saving...' : 'Save as Draft'}
                </div>
              </button>
              
              <button
                type="button"
                onClick={handlePublishClick}
                disabled={loading || !formData.title}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-md font-medium disabled:bg-gray-400"
                data-testid="publish-position-button"
              >
                {loading ? 'Publishing...' : 'Publish Position'}
              </button>
              
              <button
                type="button"
                onClick={() => navigate('/positions')}
                className="px-6 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      <GeoPublishModal
        open={showPublishModal}
        defaultSelected={defaultPublishTargets}
        organizationId={userProfile?.organizationId}
        roleIndustry={formData.industry}
        roleLocation={formData.location}
        loading={loading}
        onCancel={() => setShowPublishModal(false)}
        onConfirm={(targets, days) => handleSubmit('published', targets, days)}
      />
    </div>
  );
};

export default AddPosition;
